import {
  CallSplit,
  CropSquare,
  DeviceHub,
  FilterNone,
  MapsHomeWork,
  SvgIconComponent,
  Timeline,
  VerticalAlignCenter,
  ViewDay,
} from "@mui/icons-material";

import { LayerId, LayerIds, WORKFLOW_KEYS, WorkflowKey } from "fond/types";

export type BaseDataLayerKey = "boundary" | "addresses" | "parcels" | "streets" | "ug" | "aerial" | "leadins" | "sources";

/** Base data layers that FOND can generate. */
export type GenerationKey = Extract<BaseDataLayerKey, "ug" | "leadins">;

/** A way of populating a base data layer. */
export type BaseDataOption = "generate" | "area" | "upload" | "draw";

export type BaseDataGroupId = "designArea" | "inputs" | "candidateNetwork" | "networkPoints";

export type BaseDataLayerDefinition = {
  key: BaseDataLayerKey;
  name: string;
  /** Short lowercase name, when `name` is too long for inline copy. */
  shortName?: string;
  icon: SvgIconComponent;
  info: string;
  group: BaseDataGroupId;
  options: BaseDataOption[];
  /** Planner layers that together make up this base data layer; empty for the boundary (`version.UploadedArea`). */
  layerIds: LayerId[];
  /** Singular and plural nouns for the layer's features. */
  units: [string, string];
  /** Nouns for each of `layerIds`, when there are several. */
  layerNames?: string[];
};

export type BaseDataGroup = {
  id: BaseDataGroupId;
  label: string;
  keys: BaseDataLayerKey[];
};

/** Whether each base data layer has data. */
export type BaseDataPresence = Record<BaseDataLayerKey, boolean>;

/** Planner layers of each base data layer that have no data. */
export type BaseDataMissingLayers = Record<BaseDataLayerKey, LayerId[]>;

export const BASE_DATA_LAYERS: Record<BaseDataLayerKey, BaseDataLayerDefinition> = {
  boundary: {
    key: "boundary",
    name: "Boundary",
    icon: CropSquare,
    info: "The extent of your design area. The boundary can be used to import other datasets, to help generate service areas, and to display on construction prints.",
    group: "designArea",
    options: ["draw", "upload"],
    layerIds: [],
    units: ["polygon", "polygons"],
  },
  addresses: {
    key: "addresses",
    name: "Addresses",
    icon: MapsHomeWork,
    info: "Locations that require fiber service. Fiber allocation to each address can be controlled via the Demand model and the address type.",
    group: "inputs",
    options: ["area", "upload", "draw"],
    layerIds: [LayerIds.inAddress],
    units: ["address", "addresses"],
  },
  parcels: {
    key: "parcels",
    name: "Parcels",
    icon: FilterNone,
    info: "Polygons describing land lots. Parcels are used to generate other layers, such as underground path, leadins, and service area boundaries.",
    group: "inputs",
    options: ["area", "upload", "draw"],
    layerIds: [LayerIds.inParcel],
    units: ["parcel", "parcels"],
  },
  streets: {
    key: "streets",
    name: "Streets",
    icon: ViewDay,
    info: "Road centerlines. Streets are used to generate other layers, such as underground path, aerial spans, and service area boundaries.",
    group: "inputs",
    options: ["area", "upload", "draw"],
    layerIds: [LayerIds.inBaseDataStreet],
    units: ["street segment", "street segments"],
  },
  ug: {
    key: "ug",
    name: "Underground path & points",
    shortName: "underground",
    icon: Timeline,
    info: "All possible locations where underground fiber cables and devices may be placed in the Design step.",
    group: "candidateNetwork",
    options: ["generate", "upload", "draw"],
    layerIds: [LayerIds.inStreet, LayerIds.inStreetPoints],
    units: ["path segment", "path segments"],
    layerNames: ["path", "points"],
  },
  aerial: {
    key: "aerial",
    name: "Aerial spans & poles",
    shortName: "aerial",
    icon: VerticalAlignCenter,
    info: "All possible locations where aerial fiber cables and devices may be placed in the Design step.",
    group: "candidateNetwork",
    options: ["area", "upload", "draw"],
    layerIds: [LayerIds.inSpan, LayerIds.inPole],
    units: ["span", "spans"],
    layerNames: ["spans", "poles"],
  },
  leadins: {
    key: "leadins",
    name: "Leadins",
    icon: CallSplit,
    info: "The approximate service path from the main network to an address location.",
    group: "candidateNetwork",
    options: ["generate", "upload", "draw"],
    layerIds: [LayerIds.inLeadIns],
    units: ["leadin", "leadins"],
  },
  sources: {
    key: "sources",
    name: "Sources",
    icon: DeviceHub,
    info: "Points that feed the network. This may be a central office, distribution cabinet, or a tie-in point to an existing network. Leave empty to allow FOND to select its own locations.",
    group: "networkPoints",
    options: ["upload", "draw"],
    layerIds: [LayerIds.inExchange],
    units: ["source", "sources"],
  },
};

export const BASE_DATA_GROUPS: BaseDataGroup[] = [
  { id: "designArea", label: "DESIGN AREA", keys: ["boundary"] },
  { id: "inputs", label: "INPUTS", keys: ["addresses", "parcels", "streets"] },
  { id: "candidateNetwork", label: "CANDIDATE NETWORK", keys: ["ug", "aerial", "leadins"] },
  { id: "networkPoints", label: "NETWORK POINTS", keys: ["sources"] },
];

export const BASE_DATA_LAYER_KEYS: BaseDataLayerKey[] = BASE_DATA_GROUPS.flatMap((group) => group.keys);

/** Inputs required before any base data can be generated. */
export const REQUIRED_INPUT_KEYS = ["addresses", "parcels", "streets"] as const satisfies readonly BaseDataLayerKey[];

/** Generation workflows in the order they run. */
export const GENERATION_KEYS = ["ug", "leadins"] as const satisfies readonly GenerationKey[];

export const GENERATION_WORKFLOW_KEYS: Record<GenerationKey, WorkflowKey> = {
  ug: WORKFLOW_KEYS.PLANNER_EXTENDED.UG_GENERATION,
  leadins: WORKFLOW_KEYS.PLANNER_EXTENDED.LEADIN_GENERATION,
};

/** Return which base data layers have data, given the version's planner layer IDs and whether it has an input data area. */
export const getBaseDataPresence = (layerKeys: readonly string[], hasBoundary: boolean): BaseDataPresence =>
  Object.fromEntries(
    BASE_DATA_LAYER_KEYS.map((key) => [
      key,
      key === "boundary" ? hasBoundary : BASE_DATA_LAYERS[key].layerIds.every((layerId) => layerKeys.includes(layerId)),
    ])
  ) as BaseDataPresence;

/** Return the planner layers of each base data layer that have no data, given the version's planner layer IDs. */
export const getBaseDataMissingLayers = (layerKeys: readonly string[]): BaseDataMissingLayers =>
  Object.fromEntries(
    BASE_DATA_LAYER_KEYS.map((key) => [key, BASE_DATA_LAYERS[key].layerIds.filter((layerId) => !layerKeys.includes(layerId))])
  ) as BaseDataMissingLayers;

/** Return which base data layers have data in any of their planner layers. */
export const getBaseDataAnyPresence = (missing: BaseDataMissingLayers, hasBoundary: boolean): BaseDataPresence =>
  Object.fromEntries(
    BASE_DATA_LAYER_KEYS.map((key) => [key, key === "boundary" ? hasBoundary : missing[key].length < BASE_DATA_LAYERS[key].layerIds.length])
  ) as BaseDataPresence;
